import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { loadAllDiseasesSuccess } from 'diseases/diseases.slice';
import { loadAllLocationsSuccess } from 'locations/locations.slice';
import { orderBy, uniq, isEmpty } from 'lodash';
import {
  DefaultDiseaseActivitiesRelevantSortOptionValue,
  DiseaseActivitiesRelevantSortModel,
  DiseaseActivitesRelevantSortOptionByValue,
} from 'models/SortByOptions';
import { nameof } from 'utils/typeHelpers';

export type DiseaseActivitiesRelevantState = {
  isLoading: boolean;
  isLegendDisplayed: boolean;
  caseCountsByDiseaseId: Record<number, dto.CaseCountModel>;
  exportRisksByDiseaseId: Record<number, dto.RiskModel>;
  entriesByDiseaseIdAndLocationId: Record<
    number,
    Record<number, dto.DiseaseLocationMatrixEntryModel>
  >;
  locationNamesById: Record<number, string>;
  diseaseNamesById: Record<number, string>;
  sortBy?: dto.RiskFeedSortType;
  locationIdsSortBy: { diseaseId: number; sortBy: dto.RiskFeedSortType };
  diseaseIdsSortBy: { locationId: number; sortBy: dto.RiskFeedSortType };
  sortedLocationIds: number[];
  sortedDiseaseIds: number[];
  filteredDiseaseIds: number[];
  filteredLocationIds: number[];
};

export const InitialDiseaseActivitiesRelevantState: DiseaseActivitiesRelevantState = {
  isLoading: true,
  isLegendDisplayed: false,
  caseCountsByDiseaseId: {},
  exportRisksByDiseaseId: {},
  entriesByDiseaseIdAndLocationId: {},
  locationNamesById: {},
  diseaseNamesById: {},
  sortBy: DefaultDiseaseActivitiesRelevantSortOptionValue,
  locationIdsSortBy: null,
  diseaseIdsSortBy: null,
  sortedLocationIds: [],
  sortedDiseaseIds: [],
  filteredDiseaseIds: [],
  filteredLocationIds: [],
};

const diseaseActivitiesRelevantSlice = createSlice({
  name: nameof<InsightsState>('diseaseActivitiesRelevant'),
  initialState: InitialDiseaseActivitiesRelevantState,
  reducers: {
    loadDiseaseActivitiesRelevantSuccess: (
      state,
      action: PayloadAction<Record<number, dto.DiseaseLocationMatrixModel>[]>
    ) => {
      action.payload.forEach(locationRisksForDiseases => {
        Object.entries(locationRisksForDiseases).forEach(locationRisksForDisease => {
          const diseaseId = locationRisksForDisease[0];
          const diseaseLocationMatrixModel = locationRisksForDisease[1];
          state.caseCountsByDiseaseId[diseaseId] = diseaseLocationMatrixModel.caseCounts;
          state.exportRisksByDiseaseId[diseaseId] = diseaseLocationMatrixModel.exportationRisk;
          state.entriesByDiseaseIdAndLocationId[diseaseId] = {};
          const locationEntries = diseaseLocationMatrixModel.locationEntries;
          if (locationEntries) {
            locationEntries.forEach(it => {
              state.entriesByDiseaseIdAndLocationId[diseaseId][it.locationId] = it;
            });
          }
        });
      });
    },
    loadDiseaseActivitiesRelevantStart: state => {
      state.isLoading = true;
      state.caseCountsByDiseaseId = {};
      state.exportRisksByDiseaseId = {};
      state.entriesByDiseaseIdAndLocationId = {};
    },
    loadDiseaseActivitiesRelevantFinish: state => {
      state.isLoading = false;
    },
    sortDiseaseActivitiesRelevant: (state, action: PayloadAction<dto.RiskFeedSortType>) => {
      const sortBy = action.payload;
      const diseaseIds = Object.keys(state.diseaseNamesById).map(i => +i);
      const locationIds = Object.keys(state.locationNamesById).map(x => +x);

      if (isEmpty(locationIds) || isEmpty(diseaseIds)) {
        return;
      }

      const sortedDiseaseIds = SortDiseaseIds(
        state.entriesByDiseaseIdAndLocationId,
        sortBy,
        locationIds,
        state.diseaseNamesById,
        state.caseCountsByDiseaseId,
        state.exportRisksByDiseaseId
      );
      state.sortedDiseaseIds = sortedDiseaseIds;

      // Use the top disease's ordering of locations
      state.sortedLocationIds = SortLocationIds(
        state.entriesByDiseaseIdAndLocationId,
        sortBy,
        [sortedDiseaseIds[0]],
        state.locationNamesById
      );
      state.sortBy = sortBy;
      state.locationIdsSortBy = null;
      state.diseaseIdsSortBy = null;
    },
    sortDiseaseActivitiesRelevantLocationsForDisease: (
      state,
      action: PayloadAction<{
        diseaseId: number;
        sortBy: dto.RiskFeedSortType;
      }>
    ) => {
      const diseaseId = action.payload.diseaseId;
      const sortBy = action.payload.sortBy;
      const locationNamesById = state.locationNamesById;

      if (isEmpty(locationNamesById)) {
        return;
      }

      state.sortedLocationIds = SortLocationIds(
        state.entriesByDiseaseIdAndLocationId,
        sortBy,
        [diseaseId],
        state.locationNamesById
      );
      state.sortedDiseaseIds = uniq(
        state.sortedDiseaseIds.concat(Object.keys(state.diseaseNamesById).map(x => +x))
      );
      state.locationIdsSortBy = { diseaseId, sortBy };
      state.diseaseIdsSortBy = null;
      state.sortBy = null;
    },
    sortDiseaseActivitiesRelevantDiseasesForLocation: (
      state,
      action: PayloadAction<{
        locationId: number;
        sortBy: dto.RiskFeedSortType;
      }>
    ) => {
      const locationId = action.payload.locationId;
      const sortBy = action.payload.sortBy;
      const diseaseNamesById = state.diseaseNamesById;

      if (isEmpty(diseaseNamesById)) {
        return;
      }

      state.sortedDiseaseIds = SortDiseaseIds(
        state.entriesByDiseaseIdAndLocationId,
        sortBy,
        [locationId],
        state.diseaseNamesById,
        state.caseCountsByDiseaseId,
        state.exportRisksByDiseaseId
      );

      state.sortedLocationIds = uniq(
        state.sortedLocationIds.concat(Object.keys(state.locationNamesById).map(x => +x))
      );
      state.diseaseIdsSortBy = { locationId, sortBy };
      state.locationIdsSortBy = null;
      state.sortBy = null;
    },
    filterDiseaseActivitiesRelevantDiseases: (
      state,
      action: PayloadAction<{
        diseaseIds: number[];
      }>
    ) => {
      state.filteredDiseaseIds = action.payload.diseaseIds;
    },
    filterDiseaseActivitiesRelevantLocations: (
      state,
      action: PayloadAction<{
        locationIds: number[];
      }>
    ) => {
      state.filteredLocationIds = action.payload.locationIds;
    },
    hideDiseaseActivitiesRelevantLegend: state => {
      state.isLegendDisplayed = false;
    },
    showDiseaseActivitiesRelevantLegend: state => {
      state.isLegendDisplayed = true;
    },
  },
  extraReducers: {
    [loadAllLocationsSuccess.type]: (state, action: PayloadAction<dto.GetGeonameModel[]>) => {
      state.locationNamesById = {};
      action.payload.forEach(location => {
        state.locationNamesById[location.geonameId] = location.name;
      });
      const updatedLocationIds = action.payload.map(x => x.geonameId);
      state.sortedLocationIds = state.sortedLocationIds.filter(x => updatedLocationIds.includes(x));
      state.filteredLocationIds = state.filteredLocationIds.filter(x =>
        updatedLocationIds.includes(x)
      );
      if (
        state.diseaseIdsSortBy &&
        !updatedLocationIds.includes(state.diseaseIdsSortBy.locationId)
      ) {
        state.diseaseIdsSortBy = null;
        state.sortedDiseaseIds = [];
        state.sortBy = DefaultDiseaseActivitiesRelevantSortOptionValue;
      }
    },
    [loadAllDiseasesSuccess.type]: (
      state,
      action: PayloadAction<dto.DiseaseInformationModel[]>
    ) => {
      state.diseaseNamesById = {};
      action.payload.forEach(disease => {
        state.diseaseNamesById[disease.id] = disease.name;
      });
      const updatedDiseaseIds = action.payload.map(x => x.id);
      state.sortedDiseaseIds = state.sortedDiseaseIds.filter(x => updatedDiseaseIds.includes(x));
      state.filteredDiseaseIds = state.filteredDiseaseIds.filter(x =>
        updatedDiseaseIds.includes(x)
      );
      if (
        state.locationIdsSortBy &&
        !updatedDiseaseIds.includes(state.locationIdsSortBy.diseaseId)
      ) {
        state.locationIdsSortBy = null;
        state.sortedLocationIds = [];
        state.sortBy = DefaultDiseaseActivitiesRelevantSortOptionValue;
      }
    },
  },
});

enum EntryPriority {
  HasNoData = 1,
  HasSomeData = 2,
  HasFullData = 3,
}

/**
 * Aggregates the risk priority score, case incidences, and importation risk from
 * the entry parameter into the passed in sortModel parameter.
 * @param sortModel Initial sort model to apply aggregation into
 * @param entry Current entry to be aggregated into the sortModel parameter
 * @returns Sort model with aggregated activity
 */
const aggregateActivity = (
  sortModel: DiseaseActivitiesRelevantSortModel,
  entry: dto.DiseaseLocationMatrixEntryModel
) => {
  const result = { ...sortModel };

  // De-prioritize entries with no data by including number of entries with data for each criteria
  if (!entry) {
    return result;
  }
  result.entryPriorityScore.riskPriorityScore +=
    !entry.hasReportedData || !entry.importationRisk
      ? EntryPriority.HasNoData
      : !entry.casesPerPopulation
      ? EntryPriority.HasSomeData
      : EntryPriority.HasFullData;
  result.entryPriorityScore.caseIncidence += entry?.hasReportedData
    ? EntryPriority.HasFullData
    : EntryPriority.HasNoData;
  result.entryPriorityScore.importationRisk += entry?.importationRisk
    ? EntryPriority.HasFullData
    : EntryPriority.HasNoData;

  result.riskPriorityScore += entry?.riskPriorityScore ?? 0;
  result.caseIncidence += entry?.hasReportedData ? entry.casesPerPopulation : 0;
  result.importationRisk.maxMagnitude += entry?.importationRisk?.maxMagnitude ?? 0;
  result.importationRisk.minMagnitude += entry?.importationRisk?.minMagnitude ?? 0;
  result.importationRisk.maxProbability += entry?.importationRisk?.maxProbability ?? 0;
  result.importationRisk.minProbability += entry?.importationRisk?.minProbability ?? 0;

  return result;
};

const SortLocationIds: (
  cells: Record<number, Record<number, dto.DiseaseLocationMatrixEntryModel>>,
  sortBy: dto.RiskFeedSortType,
  diseaseIds: number[],
  locationNamesById: Record<number, string>
) => number[] = (cells, sortBy, diseaseIds, locationNamesById) => {
  const items = Object.keys(locationNamesById).map(x => {
    const locationId = +x;
    return diseaseIds.reduce(
      (acc, diseaseId) => {
        return aggregateActivity(acc, cells[diseaseId]?.[locationId]);
      },
      {
        id: locationId,
        name: locationNamesById[locationId],
        entryPriorityScore: {
          riskPriorityScore: 0,
          caseIncidence: 0,
          importationRisk: 0,
        },
        riskPriorityScore: 0,
        caseIncidence: 0,
        importationRisk: {
          maxMagnitude: 0,
          maxProbability: 0,
          minMagnitude: 0,
          minProbability: 0,
          isModelNotRun: diseaseIds.every(
            diseaseId =>
              !cells[diseaseId] ||
              !cells[diseaseId][locationId] ||
              cells[diseaseId][locationId]?.importationRisk?.isModelNotRun
          ),
          isModelRunning: diseaseIds.every(
            diseaseId =>
              cells[diseaseId] &&
              cells[diseaseId][locationId] &&
              cells[diseaseId][locationId]?.importationRisk?.isModelRunning
          ),
        },
      } as DiseaseActivitiesRelevantSortModel
    );
  });
  const sortOption = DiseaseActivitesRelevantSortOptionByValue(sortBy);

  return uniq(orderBy(items, sortOption.keys, sortOption.orders).map(it => it.id));
};

const SortDiseaseIds: (
  cells: Record<number, Record<number, dto.DiseaseLocationMatrixEntryModel>>,
  sortBy: dto.RiskFeedSortType,
  locationIds: number[],
  diseaseNamesById: Record<number, string>,
  caseCountsByDiseaseId: Record<number, dto.CaseCountModel>,
  exportRisksByDiseaseId: Record<number, dto.RiskModel>
) => number[] = (
  cells,
  sortBy,
  locationIds,
  diseaseNamesById,
  caseCountsByDiseaseId,
  exportRisksByDiseaseId
) => {
  const items = Object.keys(diseaseNamesById).map(x => {
    const diseaseId = +x;
    return locationIds.reduce(
      (acc, locationId) => {
        return aggregateActivity(acc, cells[diseaseId]?.[locationId]);
      },
      {
        id: diseaseId,
        name: diseaseNamesById[diseaseId],
        entryPriorityScore: {
          riskPriorityScore: 0,
          caseIncidence: 0,
          importationRisk: 0,
        },
        riskPriorityScore: 0,
        caseIncidence: 0,
        globalReportedCases: caseCountsByDiseaseId[diseaseId],
        exportationRisk: exportRisksByDiseaseId[diseaseId],
        importationRisk: {
          maxMagnitude: 0,
          maxProbability: 0,
          minMagnitude: 0,
          minProbability: 0,
          isModelNotRun: locationIds.every(
            locationId =>
              !cells[diseaseId] ||
              !cells[diseaseId][locationId] ||
              cells[diseaseId][locationId]?.importationRisk?.isModelNotRun
          ),
          isModelRunning: locationIds.every(
            locationId =>
              cells[diseaseId] &&
              cells[diseaseId][locationId] &&
              cells[diseaseId][locationId]?.importationRisk?.isModelRunning
          ),
        },
      } as DiseaseActivitiesRelevantSortModel
    );
  });
  const sortOption = DiseaseActivitesRelevantSortOptionByValue(sortBy);

  return uniq(orderBy(items, sortOption.keys, sortOption.orders).map(it => it.id));
};

const { actions, reducer } = diseaseActivitiesRelevantSlice;
export const {
  loadDiseaseActivitiesRelevantSuccess,
  loadDiseaseActivitiesRelevantFinish,
  loadDiseaseActivitiesRelevantStart,
  sortDiseaseActivitiesRelevant,
  sortDiseaseActivitiesRelevantLocationsForDisease,
  sortDiseaseActivitiesRelevantDiseasesForLocation,
  filterDiseaseActivitiesRelevantDiseases,
  filterDiseaseActivitiesRelevantLocations,
  hideDiseaseActivitiesRelevantLegend,
  showDiseaseActivitiesRelevantLegend,
} = actions;
export default reducer;
